import { useCallback, useMemo, useState } from 'react';
import type { BulkOutcome } from './bulk';
import { useSelection, type Selection } from './useSelection';

/** The outcome of the last batch, kept until dismissed. */
export interface BatchResult {
  /** Past tense, e.g. "deleted", "archived". */
  verb: string;
  results: BulkOutcome[];
}

export interface BulkSelection<T> {
  sel: Selection;
  /** The selected items, in list order. */
  picked: T[];
  batch: BatchResult | null;
  dismissBatch: () => void;
  /**
   * Curried so it can be handed straight to a mutation's `onSuccess`:
   * records the outcome and clears the selection.
   */
  finishBatch: (verb: string) => (results: BulkOutcome[]) => void;
}

/**
 * Everything a list with tick-boxes and bulk actions needs: the selection,
 * the picked items, and the "N deleted, 2 failed" notice state. `getId` must
 * be referentially stable (a module-level function) — it feeds the memos.
 */
export function useBulkSelection<T>(items: T[], getId: (item: T) => string): BulkSelection<T> {
  const ids = useMemo(() => items.map(getId), [items, getId]);
  const sel = useSelection(ids);
  const { selected, clear } = sel;
  const picked = useMemo(
    () => items.filter((t) => selected.has(getId(t))),
    [items, selected, getId],
  );
  const [batch, setBatch] = useState<BatchResult | null>(null);

  const finishBatch = useCallback(
    (verb: string) => (results: BulkOutcome[]) => {
      setBatch({ verb, results });
      clear();
    },
    [clear],
  );

  const dismissBatch = useCallback(() => setBatch(null), []);

  return { sel, picked, batch, dismissBatch, finishBatch };
}
